import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import GoogleButton from "../components/GoogleButton";
import Seo from "../components/Seo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifyNeeded, setVerifyNeeded] = useState(false);
  const [resendState, setResendState] = useState("idle");
  const [mfaNeeded, setMfaNeeded] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [remember, setRemember] = useState(false);

  const resendVerification = async () => {
    setResendState("sending");
    try {
      await api.post("/auth/verify/email/resend", { email: form.email });
    } catch {
      /* the response is intentionally identical either way */
    }
    setResendState("sent");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const extra = { ...(mfaNeeded ? (useRecovery ? { recovery_code: mfaCode } : { otp: mfaCode }) : {}), remember };
      const user = await login(form.email, form.password, extra);
      const next = searchParams.get("next");
      if (next) navigate(next);
      else navigate(user.role === "admin" ? "/control-tower" : "/dashboard");
    } catch (err) {
      const detail = String(err?.response?.data?.detail || "");
      if (detail.startsWith("MFA_REQUIRED")) {
        setMfaNeeded(true);
        setError(mfaNeeded ? "That code didn't match — try again." : "");
      } else if (detail.startsWith("EMAIL_VERIFICATION_REQUIRED")) {
        setVerifyNeeded(true);
        setError("");
      } else {
        setError(formatApiError(err, "Could not log you in"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="max-w-md mx-auto px-4 py-16 sm:py-24" data-testid="login-page">
      <Seo title="Log In | HiredToday" description="Log in to your HiredToday account." path="/login" />
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-zinc-950">Welcome back.</h1>
      <p className="mt-2 text-sm text-zinc-500">Log in to your account to pick up where you left off.</p>

      {error && (
        <div className="mt-6 border border-red-200 bg-red-50 text-red-800 text-sm rounded-md px-4 py-3" data-testid="login-error">
          {error}
        </div>
      )}

      {verifyNeeded && (
        <div className="mt-6 border border-amber-200 bg-amber-50 text-amber-900 text-sm rounded-md px-4 py-3" data-testid="login-verify-needed">
          <p className="font-semibold">Please verify your email address.</p>
          <p className="mt-1">Your account stays inactive until you click the verification link we emailed you.</p>
          <button type="button" onClick={resendVerification} disabled={resendState !== "idle"}
            data-testid="login-resend-verification"
            className="mt-3 h-10 px-4 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50">
            {resendState === "sent" ? "If the account needs it, a fresh link is on its way" : resendState === "sending" ? "Sending…" : "Resend verification link"}
          </button>
        </div>
      )}

      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
        <GoogleButton />
        <div className="flex items-center gap-3" aria-hidden="true">
          <span className="flex-1 h-px bg-zinc-200" />
          <span className="text-xs text-zinc-400">or with email</span>
          <span className="flex-1 h-px bg-zinc-200" />
        </div>
        <div>
          <label className="text-sm font-medium text-zinc-700 block mb-1.5">Email</label>
          <input required type="email" data-testid="login-email" value={form.email}
            autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="email"
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
        </div>
        <div>
          <label className="text-sm font-medium text-zinc-700 block mb-1.5">Password</label>
          <div className="relative">
            <input required type={showPw ? "text" : "password"} data-testid="login-password" value={form.password}
              autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="current-password"
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full h-12 px-4 pr-12 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            <button type="button" onClick={() => setShowPw(!showPw)} data-testid="login-toggle-password"
              aria-label={showPw ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-zinc-400 hover:text-zinc-700 transition-colors duration-200">
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <div className="flex justify-end mt-1.5">
            <Link to="/forgot-password" data-testid="forgot-password-link"
              className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors duration-200">
              Forgot your password?
            </Link>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-600 select-none" data-testid="login-remember-wrap">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
            data-testid="login-remember" className="h-4 w-4 rounded border-zinc-300" />
          Keep me logged in on this device
        </label>

        {mfaNeeded && (
          <div className="border border-zinc-200 bg-zinc-50 rounded-md p-4" data-testid="login-mfa-block">
            <label className="text-sm font-medium text-zinc-700 block mb-1.5">
              {useRecovery ? "Recovery code" : "Authenticator code"}
            </label>
            <input required inputMode="numeric" autoComplete="one-time-code" value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              placeholder={useRecovery ? "e.g. ab12cd34" : "6-digit code"}
              data-testid="login-mfa-code"
              className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-600" />
            <button type="button" onClick={() => { setUseRecovery(!useRecovery); setMfaCode(""); }}
              data-testid="login-mfa-toggle-recovery"
              className="text-xs font-semibold text-blue-700 hover:text-blue-900 mt-1.5">
              {useRecovery ? "Use an authenticator code instead" : "Lost your phone? Use a recovery code"}
            </button>
            <Link to="/mfa-recovery" data-testid="login-mfa-recovery-link"
              className="block text-xs text-zinc-500 hover:text-zinc-900 transition-colors duration-200 mt-1.5">
              Lost your authenticator and recovery codes?
            </Link>
          </div>
        )}
        <button type="submit" disabled={loading} data-testid="login-submit"
          className="w-full h-12 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
          {loading ? "Logging in…" : mfaNeeded ? "Verify & log in" : "Log in"}
        </button>
      </form>
      <p className="mt-6 text-sm text-zinc-500 text-center">
        No account yet?{" "}
        <Link to={`/register${searchParams.get("next") ? `?next=${encodeURIComponent(searchParams.get("next"))}` : ""}`}
          data-testid="login-to-register" className="text-blue-700 font-medium">
          Create one free
        </Link>
      </p>
    </main>
  );
}
